/* ============================================================
   WNTD THEME — JavaScript
   Wanted Customs | Drop-First Streetwear
   ============================================================ */

'use strict';

/* ── UTILITIES ──────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function pad(n) { return String(n).padStart(2, '0'); }

/* ── COUNTDOWN TIMER ────────────────────────────────────────── */
class DropCountdown {
  constructor(el) {
    this.el = el;
    this.target = new Date(el.dataset.target);
    this.days = el.querySelector('[data-countdown="days"]');
    this.hours = el.querySelector('[data-countdown="hours"]');
    this.mins = el.querySelector('[data-countdown="mins"]');
    this.secs = el.querySelector('[data-countdown="secs"]');
    if (isNaN(this.target)) return;
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  tick() {
    const diff = this.target - Date.now();
    if (diff <= 0) {
      this.el.innerHTML = '<div class="drop-countdown__live">LIVE NOW</div>';
      clearInterval(this.interval);
      return;
    }
    const totalSecs = Math.floor(diff / 1000);
    const d = Math.floor(totalSecs / 86400);
    const h = Math.floor((totalSecs % 86400) / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    if (this.days)  this.days.textContent  = pad(d);
    if (this.hours) this.hours.textContent = pad(h);
    if (this.mins)  this.mins.textContent  = pad(m);
    if (this.secs)  this.secs.textContent  = pad(s);
  }
}

/* ── MOBILE MENU ────────────────────────────────────────────── */
class MobileMenu {
  constructor() {
    this.toggle = $('.mobile-menu-toggle');
    this.nav    = $('.mobile-nav');
    if (!this.toggle || !this.nav) return;
    this.toggle.addEventListener('click', () => this.flipState());
    // close on link tap
    $$('a', this.nav).forEach(a => a.addEventListener('click', () => this.close()));
    // close on ESC
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }

  flipState() {
    const open = this.nav.classList.toggle('is-open');
    this.toggle.classList.toggle('is-active', open);
    document.body.style.overflow = open ? 'hidden' : '';
  }

  close() {
    this.nav.classList.remove('is-open');
    this.toggle.classList.remove('is-active');
    document.body.style.overflow = '';
  }
}

/* ── PRODUCT GALLERY ────────────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    this.el     = el;
    this.main   = el.querySelector('.product-gallery__main img');
    this.thumbs = $$('.product-gallery__thumb', el);
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.show(i)));
  }

  show(idx) {
    const src = this.thumbs[idx].dataset.src;
    if (!src || !this.main) return;
    this.main.src = src;
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
  }
}

/* ── VARIANT SELECTOR ───────────────────────────────────────── */
class VariantSelector {
  constructor(form) {
    this.form    = form;
    this.buttons = $$('.variant-btn', form);
    this.atcBtn  = form.querySelector('.add-to-cart');
    this.stockEl = form.querySelector('.stock-note');
    this.priceEl = form.querySelector('.product-info__price');

    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-unavailable')) return;
        // deselect siblings in same group
        const group = btn.closest('.variant-swatches');
        $$('.variant-btn', group).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.updateHidden();
      });
    });
  }

  updateHidden() {
    const selected = $$('.variant-btn.is-selected', this.form);
    // Join all selected variant values → find matching variant id
    const ids = this.form.querySelector('[name="id"]');
    const variantData = window.wntdVariants || [];
    const values = selected.map(b => b.dataset.value);
    const match = variantData.find(v => {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      return opts.every((o, i) => o === values[i]);
    });
    if (match && ids) {
      ids.value = match.id;
      const avail = match.available;
      if (this.atcBtn) {
        this.atcBtn.disabled = !avail;
        this.atcBtn.textContent = avail ? 'Add to Cart' : 'Sold Out';
      }
      if (this.stockEl) {
        this.stockEl.textContent = avail
          ? (match.inventory_quantity <= 5 ? `Only ${match.inventory_quantity} left` : 'In Stock')
          : 'Sold Out';
        this.stockEl.className = 'stock-note ' + (!avail ? 'is-out' : match.inventory_quantity <= 5 ? 'is-low' : '');
      }
      if (this.priceEl && match.price) {
        const price = (match.price / 100).toFixed(2);
        const compare = match.compare_at_price;
        this.priceEl.innerHTML = compare && compare > match.price
          ? `<del>$${(compare / 100).toFixed(2)}</del> <span class="sale">$${price}</span>`
          : `$${price}`;
      }
    }
  }
}

/* ── ADD TO CART ────────────────────────────────────────────── */
class AddToCart {
  constructor(form) {
    this.form = form;
    form.addEventListener('submit', e => {
      e.preventDefault();
      this.submit();
    });
  }

  async submit() {
    const btn = this.form.querySelector('.add-to-cart');
    const id  = this.form.querySelector('[name="id"]')?.value;
    const qty = this.form.querySelector('[name="quantity"]')?.value || 1;
    if (!id) return;

    btn.classList.add('is-loading');
    btn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: Number(qty) })
      });
      if (!res.ok) throw new Error('Add failed');
      await res.json();
      wntdToast('Added to cart');
      updateCartCount();
    } catch (err) {
      wntdToast('Something went wrong', true);
    } finally {
      btn.classList.remove('is-loading');
      btn.textContent = 'Add to Cart';
    }
  }
}

/* ── QUICK ADD (single-variant product cards) ──────────────── */
async function quickAdd(variantId) {
  if (!variantId) return;
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (!res.ok) throw new Error('Add failed');
    await res.json();
    wntdToast('Added to cart');
    updateCartCount();
  } catch (err) {
    wntdToast('Something went wrong', true);
  }
}
window.quickAdd = quickAdd;

/* ── CART COUNT UPDATE ──────────────────────────────────────── */
async function updateCartCount() {
  try {
    const res  = await fetch('/cart.js');
    const cart = await res.json();
    const badge = $('.cart-count-badge');
    if (badge) {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    }
  } catch (_) {}
}

/* ── ACCORDION ──────────────────────────────────────────────── */
class Accordion {
  constructor(el) {
    $$('.accordion__item', el).forEach(item => {
      const toggle  = item.querySelector('.accordion__toggle');
      const content = item.querySelector('.accordion__content');
      if (!toggle || !content) return;
      toggle.addEventListener('click', () => {
        const open = item.classList.toggle('is-open');
        content.style.maxHeight = open ? content.scrollHeight + 'px' : '0';
      });
    });
  }
}

/* ── TOAST NOTIFICATION ─────────────────────────────────────── */
function wntdToast(msg, isError = false) {
  let toast = $('.wntd-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'wntd-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.style.borderColor = isError ? '#c0392b' : '';
  toast.classList.add('is-visible');
  clearTimeout(toast._timeout);
  toast._timeout = setTimeout(() => toast.classList.remove('is-visible'), 3000);
}

/* ── CART LINE QTY ──────────────────────────────────────────── */
function initCartQty() {
  $$('.qty-control').forEach(ctrl => {
    const input = ctrl.querySelector('.qty-control__input');
    ctrl.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
      const v = Math.max(1, parseInt(input.value) - 1);
      input.value = v;
      updateLineQty(ctrl.dataset.line, v);
    });
    ctrl.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
      const v = parseInt(input.value) + 1;
      input.value = v;
      updateLineQty(ctrl.dataset.line, v);
    });
  });
  $$('.cart-line__remove').forEach(btn => {
    btn.addEventListener('click', () => updateLineQty(btn.dataset.line, 0));
  });
}

async function updateLineQty(line, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity: qty })
    });
    const cart = await res.json();
    if (qty === 0) location.reload();
    else {
      updateCartCount();
      const total = cart.total_price / 100;
      const totEl = $('.cart-summary__total-price');
      if (totEl) totEl.textContent = '$' + total.toFixed(2);
    }
  } catch (_) { location.reload(); }
}

/* ── FILTER PILLS ───────────────────────────────────────────── */
function initFilterPills() {
  const pills = $$('.filter-pill');
  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      pills.forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      // If you want real collection filtering, use Shopify URL params
      const tag = pill.dataset.tag;
      if (tag === 'all') {
        $$('.product-card').forEach(c => c.hidden = false);
      } else {
        $$('.product-card').forEach(c => {
          c.hidden = !c.dataset.tags?.includes(tag);
        });
      }
    });
  });
}

/* ── SCROLL HEADER SHADOW ───────────────────────────────────── */
function initScrollHeader() {
  const header = $('.site-header');
  if (!header) return;
  const observer = new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-scrolled', !entry.isIntersecting),
    { threshold: 0, rootMargin: `-${parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'))}px 0px 0px 0px` }
  );
  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none';
  document.body.prepend(sentinel);
  observer.observe(sentinel);
}

/* ── NEWSLETTER FORM ────────────────────────────────────────── */
function initNewsletterForms() {
  $$('form[action*="/contact"]').forEach(form => {
    // Klaviyo or basic Shopify newsletter
    if (!form.dataset.newsletter) return;
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const email = form.querySelector('[type="email"]')?.value;
      if (!email) return;
      const btn = form.querySelector('[type="submit"]');
      const origText = btn.textContent;
      btn.textContent = '...';
      btn.disabled = true;

      try {
        const data = new FormData(form);
        await fetch(form.action, { method: 'POST', body: data });
        btn.textContent = 'Subscribed';
        wntdToast('You\'re on the list.');
      } catch (_) {
        btn.textContent = origText;
        btn.disabled = false;
      }
    });
  });
}

/* ── INIT ───────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {

  // Countdown timers
  $$('[data-countdown-target]').forEach(el => new DropCountdown(el));

  // Mobile menu
  new MobileMenu();

  // Product gallery
  const gallery = $('.product-gallery');
  if (gallery) new ProductGallery(gallery);

  // Variant selectors
  const productForm = $('form[action="/cart/add"]');
  if (productForm) {
    new VariantSelector(productForm);
    new AddToCart(productForm);
  }

  // Accordions
  $$('.accordion').forEach(el => new Accordion(el));

  // Cart qty controls
  initCartQty();

  // Filter pills
  initFilterPills();

  // Scroll-aware header
  initScrollHeader();

  // Newsletter
  initNewsletterForms();

  // Initial cart count
  updateCartCount();
});
